import { useRef, useState } from "react";
import { FileUp, Trash2, CheckCircle2, AlertCircle } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { DATEITYP, fmtSize } from "../lib/format";
import { Select } from "./kd";

const EXT = ["jpg", "jpeg", "png", "pdf", "dcm", "zip"];
const MAX_FILE = 500 * 1024 * 1024;
const MAX_CASE = 2 * 1024 * 1024 * 1024;

type Item = { key: string; file?: File; name: string; size: number; typ: string; progress: number; state: "typ" | "laden" | "fertig" | "fehler"; error?: string; id?: string };

export function FileUploader({ uploadUrl, onChange, onUploaded, deleteUrl, existingBytes = 0, initial = [] }: {
  uploadUrl: string; onChange?: (ids: string[], items: Item[]) => void; onUploaded?: () => void; deleteUrl?: (id: string) => string; existingBytes?: number;
  initial?: { id: string; name: string; size: number; typ: string }[];
}) {
  const start: Item[] = initial.map((f) => ({ key: f.id, id: f.id, name: f.name, size: f.size, typ: f.typ, progress: 100, state: "fertig" }));
  const [items, setItems] = useState<Item[]>(start);
  const [drag, setDrag] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const itemsRef = useRef<Item[]>(start);

  const update = (key: string, patch: Partial<Item>) => {
    itemsRef.current = itemsRef.current.map((i) => (i.key === key ? { ...i, ...patch } : i));
    setItems(itemsRef.current);
    onChange?.(itemsRef.current.filter((i) => i.state === "fertig" && i.id).map((i) => i.id as string), itemsRef.current);
  };

  const add = (files: FileList | null) => {
    if (!files) return;
    let total = existingBytes + itemsRef.current.filter((i) => i.state !== "fehler").reduce((s, i) => s + i.size, 0);
    const next = Array.from(files).map((f) => {
      const ext = f.name.split(".").pop()?.toLowerCase() || "";
      const it: Item = { key: `${f.name}-${f.size}-${Math.random()}`, file: f, name: f.name, size: f.size, typ: "", progress: 0, state: "typ" };
      if (!EXT.includes(ext)) return { ...it, state: "fehler" as const, error: "Dateityp nicht erlaubt (JPG, PNG, PDF, DCM, ZIP)." };
      if (f.size > MAX_FILE) return { ...it, state: "fehler" as const, error: "Die Datei ist größer als 500 MB." };
      total += f.size;
      if (total > MAX_CASE) return { ...it, state: "fehler" as const, error: "Gesamtgröße pro Fall (2 GB) überschritten." };
      return it;
    });
    itemsRef.current = [...itemsRef.current, ...next];
    setItems(itemsRef.current);
  };

  const upload = async (it: Item, typ: string) => {
    update(it.key, { typ, state: "laden", progress: 0 });
    const fd = new FormData();
    fd.append("file", it.file as File);
    fd.append("typ", typ);
    try {
      const { data } = await api.post(uploadUrl, fd, { onUploadProgress: (e) => update(it.key, { progress: e.total ? Math.round((e.loaded / e.total) * 100) : 0 }) });
      update(it.key, { state: "fertig", progress: 100, id: data.id });
      onUploaded?.();
    } catch (e) {
      update(it.key, { state: "fehler", error: errMsg(e) });
    }
  };

  const remove = async (it: Item) => {
    if (it.state === "laden") return;
    if (it.id && deleteUrl) await api.delete(deleteUrl(it.id)).catch(() => undefined);
    itemsRef.current = itemsRef.current.filter((i) => i.key !== it.key);
    setItems(itemsRef.current);
    onChange?.(itemsRef.current.filter((i) => i.state === "fertig" && i.id).map((i) => i.id as string), itemsRef.current);
  };

  return (
    <div data-testid="file-uploader">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        className={`flex flex-col items-center justify-center gap-3 rounded-card border-2 border-dashed p-6 text-center transition-colors ${drag ? "border-primary bg-primary-light" : "border-border bg-bg"}`}
      >
        <FileUp className="h-8 w-8 text-primary" aria-hidden />
        <p>Dateien hierher ziehen oder</p>
        <button type="button" onClick={() => ref.current?.click()} className="min-h-[44px] rounded-lg border border-primary bg-surface px-4 font-medium text-primary hover:bg-primary-light" data-testid="file-select-button">
          Dateien auswählen
        </button>
        <p className="text-sm text-text-muted">JPG, PNG, PDF, DCM, ZIP · max. 500 MB pro Datei, 2 GB pro Fall</p>
        <input ref={ref} type="file" multiple hidden accept=".jpg,.jpeg,.png,.pdf,.dcm,.zip" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="file-input" />
      </div>
      {items.length > 0 && (
        <ul className="mt-4 space-y-3" data-testid="file-list">
          {items.map((it, idx) => (
            <li key={it.key} className="rounded-lg border border-border bg-surface p-3" data-testid={`file-item-${idx}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{it.name}</p>
                  <p className="text-sm text-text-muted">{fmtSize(it.size)}{it.state === "fertig" && ` · ${DATEITYP[it.typ]}`}</p>
                </div>
                <button type="button" onClick={() => remove(it)} disabled={it.state === "laden"} aria-label="Datei entfernen" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-bg hover:text-danger" data-testid={`file-remove-${idx}`}>
                  <Trash2 className="h-5 w-5" aria-hidden />
                </button>
              </div>
              {it.state === "typ" && (
                <div className="mt-2">
                  <label className="kd-label" htmlFor={`typ-${it.key}`}>Dateityp wählen, um den Upload zu starten</label>
                  <Select id={`typ-${it.key}`} options={DATEITYP} placeholder="Bitte wählen …" value="" onChange={(e) => e.target.value && upload(it, e.target.value)} data-testid={`file-type-select-${idx}`} />
                </div>
              )}
              {(it.state === "laden" || it.state === "fertig") && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-bg" role="progressbar" aria-valuenow={it.progress} aria-valuemin={0} aria-valuemax={100}>
                    <div className="h-full bg-primary transition-[width] duration-200" style={{ width: `${it.progress}%` }} />
                  </div>
                  {it.state === "fertig" ? <CheckCircle2 className="h-5 w-5 text-success" aria-label="Hochgeladen" /> : <span className="text-sm text-text-muted">{it.progress} %</span>}
                </div>
              )}
              {it.state === "fehler" && <p className="mt-2 flex items-center gap-1 text-sm text-danger"><AlertCircle className="h-4 w-4" aria-hidden />{it.error}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
